import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Mail, Search, Download, TrendingUp, UserCheck, UserX, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface Subscriber {
  id: string;
  email: string;
  name: string | null;
  status: string;
  is_verified: boolean;
  subscribed_at: string;
  unsubscribed_at: string | null;
}

const NewsletterManagement = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const statusOptions = [
    { value: 'all', label: 'Todos los Estados' },
    { value: 'active', label: 'Activos' },
    { value: 'unsubscribed', label: 'Dados de Baja' },
    { value: 'bounced', label: 'Rebotados' }
  ];

  useEffect(() => {
    loadSubscribers();
  }, [statusFilter]);

  const loadSubscribers = async () => {
    try {
      let query = supabase
        .from('newsletter_subscribers')
        .select('*')
        .order('subscribed_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setSubscribers(data || []);
    } catch (error) {
      console.error('Error al cargar suscriptores:', error);
      toast.error('Error al cargar los suscriptores');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (subscriberId: string, newStatus: string) => {
    try {
      const updates: any = { status: newStatus };
      
      if (newStatus === 'unsubscribed') {
        updates.unsubscribed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('newsletter_subscribers')
        .update(updates)
        .eq('id', subscriberId);

      if (error) throw error;

      toast.success('Estado actualizado correctamente');
      loadSubscribers();
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      toast.error('Error al actualizar el estado');
    }
  };

  const handleExportCSV = () => {
    const filteredSubs = filteredSubscribers;
    
    // Crear CSV con BOM para Excel
    const BOM = '\uFEFF';
    const headers = ['Email', 'Nombre', 'Estado', 'Verificado', 'Fecha Suscripción', 'Fecha Baja'];
    const rows = filteredSubs.map(sub => [
      sub.email,
      sub.name || 'N/A',
      sub.status,
      sub.is_verified ? 'Sí' : 'No',
      new Date(sub.subscribed_at).toLocaleString('es-ES'),
      sub.unsubscribed_at ? new Date(sub.unsubscribed_at).toLocaleString('es-ES') : 'N/A'
    ]);

    const csvContent = BOM + [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `newsletter_subscribers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`${filteredSubs.length} suscriptores exportados a CSV`);
  };

  const filteredSubscribers = subscribers.filter(sub =>
    sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (sub.name && sub.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { color: string; label: string; icon: any }> = {
      active: { color: 'bg-green-100 text-green-800', label: 'Activo', icon: UserCheck },
      unsubscribed: { color: 'bg-gray-100 text-gray-800', label: 'Dado de Baja', icon: UserX },
      bounced: { color: 'bg-red-100 text-red-800', label: 'Rebotado', icon: AlertCircle }
    };

    const badge = badges[status] || badges.active;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const stats = {
    total: subscribers.length,
    active: subscribers.filter(s => s.status === 'active').length,
    verified: subscribers.filter(s => s.is_verified).length,
    unsubscribed: subscribers.filter(s => s.status === 'unsubscribed').length
  };

  const growthRate = stats.total > 0 ? ((stats.active / stats.total) * 100).toFixed(1) : '0';

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestión de Newsletter</h1>
        <p className="text-gray-600">Administra los suscriptores del newsletter y visualiza estadísticas</p>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm opacity-90">Total Suscriptores</p>
            <Mail className="w-6 h-6 opacity-80" />
          </div>
          <p className="text-4xl font-bold">{stats.total}</p>
          <p className="text-xs mt-2 opacity-75">Desde el inicio</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm opacity-90">Suscriptores Activos</p>
            <UserCheck className="w-6 h-6 opacity-80" />
          </div>
          <p className="text-4xl font-bold">{stats.active}</p>
          <p className="text-xs mt-2 opacity-75">{growthRate}% del total</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm opacity-90">Emails Verificados</p>
            <TrendingUp className="w-6 h-6 opacity-80" />
          </div>
          <p className="text-4xl font-bold">{stats.verified}</p>
          <p className="text-xs mt-2 opacity-75">{stats.total > 0 ? ((stats.verified / stats.total) * 100).toFixed(1) : '0'}% verificados</p>
        </div>

        <div className="bg-gradient-to-br from-gray-500 to-gray-600 text-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm opacity-90">Dados de Baja</p>
            <UserX className="w-6 h-6 opacity-80" />
          </div>
          <p className="text-4xl font-bold">{stats.unsubscribed}</p>
          <p className="text-xs mt-2 opacity-75">{stats.total > 0 ? ((stats.unsubscribed / stats.total) * 100).toFixed(1) : '0'}% tasa de baja</p>
        </div>
      </div>

      {/* Filtros y Acciones */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex-1 flex gap-4 w-full md:w-auto">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por email o nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <button
            onClick={handleExportCSV}
            disabled={filteredSubscribers.length === 0}
            className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
          >
            <Download className="w-4 h-4" />
            Exportar CSV ({filteredSubscribers.length})
          </button>
        </div>
      </div>

      {/* Tabla de Suscriptores */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando suscriptores...</p>
          </div>
        ) : filteredSubscribers.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay suscriptores</h3>
            <p className="text-gray-600">
              {searchTerm ? 'No se encontraron suscriptores con ese criterio de búsqueda' : 'Aún no hay suscriptores en el newsletter'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Verificado
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Fecha Suscripción
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredSubscribers.map((subscriber) => (
                  <tr key={subscriber.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-3">
                          {subscriber.email.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {subscriber.email}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {subscriber.name || <span className="text-gray-400 italic">Sin nombre</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(subscriber.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {subscriber.is_verified ? (
                        <span className="text-green-600 font-medium text-sm">✓ Verificado</span>
                      ) : (
                        <span className="text-gray-400 text-sm">No verificado</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(subscriber.subscribed_at).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {subscriber.status === 'active' ? (
                        <button
                          onClick={() => handleUpdateStatus(subscriber.id, 'unsubscribed')}
                          className="text-red-600 hover:text-red-800 font-medium"
                        >
                          Dar de Baja
                        </button>
                      ) : subscriber.status === 'unsubscribed' ? (
                        <button
                          onClick={() => handleUpdateStatus(subscriber.id, 'active')}
                          className="text-green-600 hover:text-green-800 font-medium"
                        >
                          Reactivar
                        </button>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Información Adicional */}
      <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-blue-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-semibold text-blue-900">Próximas Funcionalidades</h3>
            <div className="mt-2 text-sm text-blue-800">
              <ul className="list-disc list-inside space-y-1">
                <li>Envío de emails masivos a suscriptores</li>
                <li>Segmentación de audiencia por intereses</li>
                <li>Plantillas de newsletter personalizables</li>
                <li>Análisis de tasas de apertura y clics</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterManagement;
